import { ReceiptText } from 'lucide-react';
import { PageHeader } from '@/shared/components/ui/PageHeader';
import { useSearchParams } from 'react-router-dom';
import { InvoiceList } from '../components/InvoiceList';
import { CreditNotesList } from '../components/CreditNotesList';
import { PaymentList } from '../components/PaymentList';
import { ModuleGuide } from '@/shared/components/ui/ModuleGuide';
import { CommercialDocumentsList } from '../components/CommercialDocumentsList';
import { RecurringProfilesList } from '../components/RecurringProfilesList';

// Quotes and Sales Orders are NOT here on purpose. Both tabs read the parallel
// commercial-document engine (/api/accounts/commercial-documents/quotes and
// .../sales-orders), which is permanently empty: the live pipeline writes
// quotations and sales orders to SalesOrderModel instead, and those are already
// on their own Sales -> Quotations / Sales Operations screens. Shipping two
// permanently-blank tabs beside the real ones only invites the question of
// which list is the true one. Same reasoning that took Quote-to-Cash off the
// sidebar. The routes still resolve; nothing was deleted, and
// CommercialDocumentsList still serves those collections for Purchases.
const TABS = [
  { id: 'invoices', label: 'Sales Invoices' },
  { id: 'recurring-invoices', label: 'Recurring Invoices' },
  { id: 'delivery-challans', label: 'Delivery Challans' },
  { id: 'credit-notes', label: 'Credit Notes' },
  { id: 'receipts', label: 'Receipts' },
];

export function SalesPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const requestedTab = searchParams.get('tab');
  const tab = TABS.some((item) => item.id === requestedTab) ? requestedTab! : 'invoices';
  const setTab = (next: string) => setSearchParams({ tab: next });

  return (
    <div className="space-y-4 pb-16">
      <PageHeader icon={ReceiptText} eyebrow="Finance" title="Sales" description={<>Invoices, credit notes and payment receipts</>} />
      <ModuleGuide
        summary="Invoice customers and receive their payments. A posted invoice becomes a receivable; posting a receipt updates the bank/cash balance and the customer's outstanding due."
        steps={['Draft the invoice and verify items and rates.', 'Finalize/post the invoice → records sales, GST and the customer receivable.', 'Record the receipt → increases bank/cash and reduces the linked invoice due.']}
        formulas={[
          { label: 'Invoice total', formula: 'Subtotal − Discount + CGST + SGST + IGST + Round off' },
          { label: 'Amount due', formula: 'Invoice total − Linked receipts − Credit notes' },
        ]}
      />
      <div className="flex gap-2 border-b">
        {TABS.map(t => (
          <button key={t.id} onClick={() => setTab(t.id)}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${tab === t.id ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground'}`}>
            {t.label}
          </button>
        ))}
      </div>
      {tab === 'invoices' && <InvoiceList />}
      {tab === 'recurring-invoices' && <RecurringProfilesList kind="invoice" />}
      {/* allowCreate={false}: nobody types a challan from scratch any more. The
          dispatch record is raised when Sales hands an order to Dispatch, and
          the challan document is issued from the Invoices screen. A free-standing
          challan carried no salesOrderModelId, so it was invisible to the
          Dispatch queue and to every duplicate guard. */}
      {tab === 'delivery-challans' && <CommercialDocumentsList collection="delivery-challans" allowCreate={false} />}
      {tab === 'credit-notes' && <CreditNotesList type="credit" />}
      {tab === 'receipts' && <PaymentList />}
    </div>
  );
}
